import { Link } from "react-router-dom";
import { ClipboardList, CalendarClock, FileCheck2, Phone, Mail, MapPin, Clock, Leaf } from "lucide-react";
import { useFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Brand } from "@/layouts/CustomerLayout";

const STEPS = [
  [ClipboardList, "Send your request", "Tell us about the work and upload a few photos of the area."],
  [CalendarClock, "Book a consultation", "Choose a telephone, video or on-site slot that suits you."],
  [FileCheck2, "Receive your quote", "View the itemised quotation online and accept it when you're ready."],
];

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
    <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p>{value || "[Not yet provided]"}</p></div>
  </div>
);

export default function Start() {
  const { data } = useFetch("/public/info");
  const services = data?.services || [];
  // with an odd count, the item before the final pair spans the full row so the grid ends evenly
  const spanIdx = services.length % 2 ? services.length - 3 : -1;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Brand sub="Job booking portal" />
          <Button asChild variant="outline" size="sm" data-testid="start-signin-btn"><Link to="/login">Sign in</Link></Button>
        </div>
      </header>
      <section className="relative isolate overflow-hidden bg-primary" data-testid="start-hero">
        <img src="/hero-garden.jpg" alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover object-[center_85%]" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[hsl(152_48%_10%/0.85)] via-[hsl(152_48%_10%/0.55)] to-[hsl(152_48%_10%/0.2)]" />
        <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-16 sm:px-6 sm:py-24 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl text-white">
            <h1 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" data-testid="start-title">Book landscaping work with {data?.business_name || "DJ Landscapes"}</h1>
            <p className="mt-4 text-base text-white/85 md:text-lg">Request a job, book a consultation and track your quote and job progress in one secure portal.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[#d4a85a] text-[#0f2a1b] hover:bg-[#c99b4b]" data-testid="start-book-btn"><Link to="/register?next=/portal/new">Book a job</Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="start-signin-btn-2"><Link to="/login">I already have an account</Link></Button>
            </div>
          </div>
        </div>
      </section>
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <section aria-labelledby="how">
          <h2 id="how" className="font-heading text-base font-semibold md:text-lg">How it works</h2>
          <ol className="mt-4 grid gap-4 sm:grid-cols-3">
            {STEPS.map(([Icon, t, d], i) => (
              <li key={t} tabIndex={0} data-testid={`start-step-${i}`}
                className="group cursor-default rounded-lg border border-border bg-card p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-primary hover:shadow-lg focus-visible:-translate-y-1.5 focus-visible:border-primary">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground"><Icon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" /></span>
                <p className="mt-3 font-medium transition-colors group-hover:text-primary">{i + 1}. {t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
              </li>))}
          </ol>
        </section>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          <section aria-labelledby="svc">
            <h2 id="svc" className="font-heading text-base font-semibold md:text-lg">Services</h2>
            <ul className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-border bg-card p-3" data-testid="start-services">
              {services.map((s, i) => (
                <li key={s} tabIndex={0} data-testid={`start-service-${i}`}
                  className={`group flex cursor-default items-center gap-2 rounded-md border border-border bg-secondary/60 px-3 py-2.5 text-sm text-secondary-foreground transition-[transform,background-color,color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground hover:shadow-md focus-visible:bg-primary focus-visible:text-primary-foreground ${i === spanIdx ? "col-span-2" : ""}`}>
                  <Leaf className="h-4 w-4 shrink-0 text-primary transition-colors group-hover:text-[#d4a85a]" />{s}
                </li>))}
            </ul>
          </section>
          <section aria-labelledby="contact" className="space-y-4 rounded-lg border border-border bg-card p-5" data-testid="start-contact">
            <h2 id="contact" className="font-heading text-base font-semibold md:text-lg">Contact</h2>
            <Info icon={Phone} label="Telephone" value={data?.phone} />
            <Info icon={Mail} label="Email" value={data?.email} />
            <Info icon={MapPin} label="Service area" value={data?.service_area} />
            <Info icon={Clock} label="Opening hours" value={data?.opening_hours} />
            <Info icon={CalendarClock} label="Consultation availability" value={data?.consultation_info} />
          </section>
        </div>
      </main>
      <footer className="mx-auto max-w-5xl px-4 pb-10 text-xs text-muted-foreground sm:px-6">
        <Link to="/privacy" className="underline" data-testid="start-privacy-link">Privacy information</Link>
      </footer>
    </div>
  );
}
